import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/useToast';
import './AuditTrail.css';

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

// Map an action verb (or one of its aliases) to a CSS modifier so we can
// color-code the badge in the table. Falls back to a neutral grey.
function actionVariant(action = '') {
  const a = action.toLowerCase();
  if (a.includes('create') || a.includes('imported') || a.includes('weaned')) return 'create';
  if (a.includes('archive') || a.includes('cancel')) return 'archive';
  if (a.includes('restore')) return 'restore';
  if (a.includes('transfer')) return 'transfer';
  if (a.includes('delete')) return 'delete';
  if (a.includes('update')) return 'update';
  return 'default';
}

// Human-readable verb: 'mouse_archived' -> 'Mouse archived'
function humanizeAction(action = '') {
  const cleaned = action.replace(/_/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

// Short relative time string ("2m ago", "3h ago", "4d ago")
function relativeTime(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Compare oldValues / newValues and return a stable list of changed-only entries.
// Skips noisy timestamp fields the user can't act on.
const NOISE_KEYS = new Set(['updated_at', 'created_at']);
function computeDiff(oldVal, newVal, idLabels = {}) {
  const parse = v => (typeof v === 'string' ? safeParse(v) : v) || {};
  const oldObj = parse(oldVal);
  const newObj = parse(newVal);
  const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
  const rows = [];
  allKeys.forEach(key => {
    if (NOISE_KEYS.has(key)) return;
    const o = oldObj[key];
    const n = newObj[key];
    const changed = JSON.stringify(o) !== JSON.stringify(n);
    const labelMap = idLabels[key];
    rows.push({
      key,
      oldVal: o,
      newVal: n,
      changed,
      oldLabel: labelMap && o != null ? labelMap[String(o)] : undefined,
      newLabel: labelMap && n != null ? labelMap[String(n)] : undefined,
    });
  });
  // Changed rows first, then unchanged in alphabetical order
  rows.sort((a, b) => {
    if (a.changed !== b.changed) return a.changed ? -1 : 1;
    return a.key.localeCompare(b.key);
  });
  return rows;
}

function safeParse(s) {
  try { return JSON.parse(s); } catch { return null; }
}

function formatVal(v, label) {
  if (v === null || v === undefined) return '∅';
  let base;
  if (typeof v === 'object') base = JSON.stringify(v);
  else base = String(v);
  return label ? `${base} (${label})` : base;
}

function countChanges(oldVal, newVal) {
  if (!oldVal && !newVal) return 0;
  return computeDiff(oldVal, newVal).filter(r => r.changed).length;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export default function AuditTrail() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const toast = useToast();
  const [selectedLog, setSelectedLog] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState({
    filter_resource_type: '',
    filter_from_date: '',
    filter_to_date: '',
    search: ''
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const loadAuditLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        _page: pagination.page,
        _limit: pagination.limit,
        _sort: 'created_at',
        _order: 'desc',
        ...filters,
      };
      const res = await axios.get(`${API_URL}/api/audit`, { params });
      setAuditLogs(res.data.data || []);
      setPagination(prev => ({ ...prev, ...res.data.pagination }));
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  }, [API_URL, filters, pagination.page, pagination.limit]);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  // Reset to page 1 whenever a filter changes (but not on page changes themselves)
  useEffect(() => {
    setPagination(p => (p.page === 1 ? p : { ...p, page: 1 }));
  }, [filters]);

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/audit/export`, {
        params: filters,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_trail_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      // Export failure is an action result, not a page state. The old code
      // put it in the same banner as a load failure, where it sat above a
      // perfectly good table reading as if the table had failed — and stayed
      // there until the next fetch cleared it.
      toast.error(err.response?.data?.error || 'Failed to export audit trail');
    }
  };

  const clearFilters = () =>
    setFilters({ filter_resource_type: '', filter_from_date: '', filter_to_date: '', search: '' });

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some(v => v && String(v).trim() !== ''),
    [filters]
  );

  // ----- Diff renderer (used in modal) -----
  const renderDiff = (oldVal, newVal, idLabels = {}) => {
    const rows = computeDiff(oldVal, newVal, idLabels);
    if (rows.length === 0) return null;
    return (
      <div className="audit-trail__diff">
        {rows.map(({ key, oldVal: o, newVal: n, changed, oldLabel, newLabel }) => (
          <div
            key={key}
            className={`audit-trail__diff-row ${changed ? 'audit-trail__diff-row--changed' : ''}`}
          >
            <div className="audit-trail__diff-key">{key}</div>
            <div className="audit-trail__diff-vals">
              {changed ? (
                <>
                  {o !== undefined && <span className="audit-trail__diff-old">{formatVal(o, oldLabel)}</span>}
                  {n !== undefined && <span className="audit-trail__diff-new">{formatVal(n, newLabel)}</span>}
                </>
              ) : (
                <span className="audit-trail__diff-unchanged">{formatVal(o ?? n, oldLabel ?? newLabel)}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="audit-trail">
      <div className="audit-trail__header">
        <div>
          <h2>Audit Trail</h2>
          <div className="audit-trail__meta">
            {pagination.total > 0
              ? `${pagination.total.toLocaleString()} entries`
              : loading ? 'Loading…' : 'No entries'}
          </div>
        </div>
        <button className="primary-button" onClick={handleExport} disabled={loading}>
          Export CSV
        </button>
      </div>

      {error && <div className="audit-trail__error">{error}</div>}

      {/* Filters */}
      <div className="audit-trail__filters">
        <input
          type="text"
          placeholder="Search username, action, or resource…"
          value={filters.search}
          onChange={e => setFilters({ ...filters, search: e.target.value })}
        />
        <select
          value={filters.filter_resource_type}
          onChange={e => setFilters({ ...filters, filter_resource_type: e.target.value })}
        >
          <option value="">All resources</option>
          <option value="mice">Mice</option>
          <option value="cages">Cages</option>
          <option value="litters">Litters</option>
          <option value="pregnancies">Pregnancies</option>
          <option value="user">Users</option>
          <option value="lab">Labs</option>
        </select>
        <input
          type="date"
          value={filters.filter_from_date}
          onChange={e => setFilters({ ...filters, filter_from_date: e.target.value })}
          aria-label="From date"
        />
        <input
          type="date"
          value={filters.filter_to_date}
          onChange={e => setFilters({ ...filters, filter_to_date: e.target.value })}
          aria-label="To date"
        />
        <span />{/* spacer for grid alignment */}
        <button
          className="audit-trail__clear"
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          title="Clear all filters"
        >
          Clear
        </button>
      </div>

      {/* Table */}
      <div className="audit-trail__table-wrap">
        <table className="audit-trail__table">
          <thead>
            <tr>
              <th>When</th>
              <th>User</th>
              <th>Action</th>
              <th>Resource</th>
              <th>Changes</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="audit-trail__empty">Loading…</td></tr>
            ) : auditLogs.length === 0 ? (
              <tr><td colSpan={5} className="audit-trail__empty">No audit entries match these filters.</td></tr>
            ) : (
              auditLogs.map(log => {
                const changes = countChanges(log.old_values, log.new_values);
                const detailKeys = log.details ? Object.keys(log.details).length : 0;
                const fullName = [log.first_name, log.last_name].filter(Boolean).join(' ');
                return (
                  <tr key={log.id} onClick={() => setSelectedLog(log)}>
                    <td className="audit-trail__time">
                      {new Date(log.created_at).toLocaleString()}
                      <span className="audit-trail__time-rel">{relativeTime(log.created_at)}</span>
                    </td>
                    <td>
                      <span className="audit-trail__user">{fullName || log.username || 'Unknown'}</span>
                      {fullName && log.username && (
                        <span className="audit-trail__user-handle">@{log.username}</span>
                      )}
                    </td>
                    <td>
                      <span className={`audit-trail__action audit-trail__action--${actionVariant(log.action)}`}>
                        {humanizeAction(log.action)}
                      </span>
                    </td>
                    <td>
                      <span className="audit-trail__resource">{log.resource_type || '—'}</span>
                      {log.resource_id ? (
                        log.id_labels?.__resource
                          ? ` ${log.id_labels.__resource}`
                          : ` #${log.resource_id}`
                      ) : ''}
                    </td>
                    <td className="audit-trail__changes-count">
                      {changes > 0 ? (
                        <><strong>{changes}</strong> field{changes === 1 ? '' : 's'} changed</>
                      ) : detailKeys > 0 ? (
                        <>{detailKeys} detail{detailKeys === 1 ? '' : 's'}</>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="audit-trail__pagination">
        <button
          disabled={pagination.page <= 1 || loading}
          onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
        >
          ← Previous
        </button>
        <span className="audit-trail__pagination-info">
          Page {pagination.page} of {pagination.totalPages || 1}
        </span>
        <button
          disabled={pagination.page >= (pagination.totalPages || 1) || loading}
          onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
        >
          Next →
        </button>
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="audit-trail__modal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="audit-trail__modal" onClick={e => e.stopPropagation()}>
            <div className="audit-trail__modal-header">
              <h3>
                <span className={`audit-trail__action audit-trail__action--${actionVariant(selectedLog.action)}`}>
                  {humanizeAction(selectedLog.action)}
                </span>
                {' '}
                <span className="audit-trail__resource">{selectedLog.resource_type}</span>
                {selectedLog.resource_id ? (
                  selectedLog.id_labels?.__resource
                    ? ` ${selectedLog.id_labels.__resource} (#${selectedLog.resource_id})`
                    : ` #${selectedLog.resource_id}`
                ) : ''}
              </h3>
              <button className="audit-trail__modal-close" onClick={() => setSelectedLog(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="audit-trail__modal-body">
              <div className="audit-trail__detail-section">
                <h4>Basic information</h4>
                <dl className="audit-trail__detail-grid">
                  <dt>Log ID</dt><dd>{selectedLog.id}</dd>
                  <dt>Timestamp</dt><dd>{new Date(selectedLog.created_at).toLocaleString()}</dd>
                  <dt>User</dt>
                  <dd>
                    {[selectedLog.first_name, selectedLog.last_name].filter(Boolean).join(' ') || 'Unknown'}
                    {selectedLog.username ? ` (@${selectedLog.username})` : ''}
                  </dd>
                  <dt>IP address</dt><dd>{selectedLog.ip_address || '—'}</dd>
                  {selectedLog.request_id && (<><dt>Request ID</dt><dd>{selectedLog.request_id}</dd></>)}
                </dl>
              </div>

              {(selectedLog.old_values || selectedLog.new_values) && (
                <div className="audit-trail__detail-section">
                  <h4>Changes</h4>
                  {renderDiff(selectedLog.old_values, selectedLog.new_values, selectedLog.id_labels)}
                </div>
              )}

              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div className="audit-trail__detail-section">
                  <h4>Additional details</h4>
                  <pre className="audit-trail__details-pre">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
